import React, { useState } from 'react'
import AuthLink from '../../Auth/AuthLink'
import { Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Menu, LogOut, UserPlus, LogIn, Sun, Moon, Monitor, User, ChevronDown, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { logOut } from '../../../redux/actions'
import history from '../../../const/history'
import siteUrl from '@/const/const'
import { useTheme, type Theme } from '@/hooks/useTheme'
import { isFeatureEnabled } from '../../../services/flagsService'
import { TOOL_GROUPS, TOOL_TONES, availableTools, toolsInGroup } from '../../../const/tools'


const NAV_LINKS = (t: (k: string) => string) => {
  const links = [
    { to: '/', label: t('header.mainPage') },
    { to: '/search', label: t('header.search') },
    { to: '/authors', label: t('header.authors') },
  ]
  if (isFeatureEnabled('RATING_MENU')) {
    links.push({ to: '/ratings', label: 'Reytinq' })
  }
  return links
}

const THEME_CYCLE: Theme[] = ['light', 'dark', 'system']
const THEME_ICONS = { light: Sun, dark: Moon, system: Monitor }
const THEME_LABELS = { light: 'Açıq', dark: 'Tünd', system: 'Sistem' }

const Header: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector((state: any) => state.user)
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const accountsEnabled = isFeatureEnabled('USER_ACCOUNTS')

  // Leave first so a protected page isn't still mounted (and asking to sign in) on logout.
  const handleLogOut = () => {
    history.push('/')
    dispatch(logOut() as any)
  }

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  const toolsActive = isActive('/tools') || availableTools().some(tool => isActive(tool.to))

  const isImmersiveTimerPage = location.pathname === '/timer'
  if (isImmersiveTimerPage) return null

  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(theme)
    setTheme(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length])
  }

  const ThemeIcon = THEME_ICONS[theme]

  return (
    <header className="fixed top-0 left-0 right-0 z-[3000] bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img
              src="/logo-horizontal.png"
              alt="3sual.az"
              className="h-8 w-auto object-contain dark:hidden"
            />
            <img
              src="/logo-horizontal-dark.png"
              alt=""
              aria-hidden="true"
              className="hidden h-8 w-auto object-contain dark:block"
            />
          </Link>

          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS(t).map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={cn(
                  "px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150",
                  isActive(to)
                    ? "bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-indigo-300"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
                )}
              >
                {label}
              </Link>
            ))}
            <DropdownMenu.Root modal={false}>
              <DropdownMenu.Trigger
                className={cn(
                  "group inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 outline-none",
                  toolsActive
                    ? "bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-indigo-300"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800"
                )}
              >
                Alətlər
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-data-[state=open]:rotate-180" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="center"
                  sideOffset={10}
                  className="z-[3001] w-[480px] rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-2 shadow-xl animate-in fade-in-0 zoom-in-95"
                >
                  <div className="grid grid-cols-2 gap-1">
                    {TOOL_GROUPS.map(group => (
                      <DropdownMenu.Group key={group.id} className="min-w-0">
                        <DropdownMenu.Label className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                          {group.title}
                        </DropdownMenu.Label>
                        {toolsInGroup(group.id).map(({ id, to, title, icon: Icon, tone }) => (
                          <DropdownMenu.Item key={id} asChild>
                            <Link
                              to={to}
                              className="group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 outline-none hover:bg-gray-50 dark:hover:bg-gray-800 data-[highlighted]:bg-gray-50 dark:data-[highlighted]:bg-gray-800"
                            >
                              <span className={cn('flex-shrink-0 rounded-md p-1.5 transition-colors', TOOL_TONES[tone].tile)}>
                                <Icon className="w-3.5 h-3.5" />
                              </span>
                              <span className="truncate">{title}</span>
                            </Link>
                          </DropdownMenu.Item>
                        ))}
                      </DropdownMenu.Group>
                    ))}
                  </div>
                  <DropdownMenu.Separator className="my-2 h-px bg-gray-100 dark:bg-gray-800" />
                  <DropdownMenu.Item asChild>
                    <Link
                      to="/tools"
                      className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold text-blue-600 dark:text-indigo-300 outline-none hover:bg-blue-50 dark:hover:bg-blue-950 data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-blue-950"
                    >
                      Bütün alətlər
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </nav>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-2">
            {/* Theme toggle */}
            <button
              onClick={cycleTheme}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              title={THEME_LABELS[theme]}
            >
              <ThemeIcon className="w-4 h-4" />
            </button>

            {!accountsEnabled ? null : user.isLoggedIn ? (
              <div className="flex items-center gap-2">
                <Link to="/profile">
                  <img
                    src={`${siteUrl}${user.data?.photo}` || `${siteUrl}images/avatar.jpg`}
                    alt="avatar"
                    className="w-8 h-8 rounded-lg object-cover ring-2 ring-gray-100 dark:ring-gray-800 hover:ring-blue-300 transition"
                  />
                </Link>
                <button
                  onClick={handleLogOut}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:text-red-500 hover:border-red-200 dark:hover:border-red-800 transition-colors"
                  title="Çıxış"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Button asChild size="sm" variant="ghost" className="text-gray-600 dark:text-gray-300">
                  <AuthLink mode="login" aria-label={t('header.login') || 'Giriş'}>
                    <LogIn className="w-4 h-4" />
                  </AuthLink>
                </Button>
                <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <AuthLink mode="register" aria-label={t('header.register') || 'Qeydiyyat'}>
                    <UserPlus className="w-4 h-4" />
                  </AuthLink>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile theme toggle */}
            <button
              onClick={cycleTheme}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
              aria-label="Toggle theme"
            >
              <ThemeIcon className="w-5 h-5" />
            </button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col dark:bg-gray-900 dark:border-gray-800">
                <SheetHeader className="px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
                  <SheetTitle className="flex items-center">
                    <img
                      src="/logo-horizontal.png"
                      alt="3sual.az"
                      className="h-7 w-auto object-contain dark:hidden"
                    />
                    <img
                      src="/logo-horizontal-dark.png"
                      alt=""
                      aria-hidden="true"
                      className="hidden h-7 w-auto object-contain dark:block"
                    />
                  </SheetTitle>
                </SheetHeader>

                <nav className="flex flex-col px-3 py-4 gap-1 flex-1 overflow-y-auto">
                  {NAV_LINKS(t).map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center px-4 py-3 rounded-xl text-base font-medium transition-colors",
                        isActive(to)
                          ? "bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-indigo-300"
                          : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      {label}
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    {t('header.contact')}
                  </Link>
                  {TOOL_GROUPS.map(group => {
                    const tools = toolsInGroup(group.id)
                    if (tools.length === 0) return null
                    return (
                      <div key={group.id} className="pt-3">
                        <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                          {group.title}
                        </p>
                        {tools.map(({ id, to, title, icon: Icon, tone }) => (
                          <Link
                            key={id}
                            to={to}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                              "flex items-center gap-3 px-4 py-2.5 rounded-xl text-base font-medium transition-colors",
                              isActive(to)
                                ? "bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-indigo-300"
                                : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                            )}
                          >
                            <Icon className={cn('w-4 h-4 flex-shrink-0', TOOL_TONES[tone].chip)} />
                            {title}
                          </Link>
                        ))}
                      </div>
                    )
                  })}
                  {accountsEnabled && (
                    <Link
                      to={user.isLoggedIn ? '/profile' : '/login'}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors",
                        isActive('/profile')
                          ? "bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-indigo-300"
                          : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      {user.isLoggedIn ? (
                        <img
                          src={`${siteUrl}${user.data?.photo}` || `${siteUrl}images/avatar.jpg`}
                          alt="avatar"
                          className="w-6 h-6 rounded-md object-cover flex-shrink-0"
                        />
                      ) : (
                        <User className="w-5 h-5 flex-shrink-0" />
                      )}
                      {user.isLoggedIn ? (user.data?.name || t('header.profile')) : t('header.profile')}
                    </Link>
                  )}
                </nav>

                {accountsEnabled && (
                  <div className="px-4 pb-5 border-t border-gray-100 dark:border-gray-800 pt-4 space-y-2">
                    {user.isLoggedIn ? (
                      <>
                        <Link
                          to="/profile"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          <img
                            src={`${siteUrl}${user.data?.photo}` || `${siteUrl}images/avatar.jpg`}
                            alt="avatar"
                            className="w-9 h-9 rounded-lg object-cover"
                          />
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {user.data?.name || 'Profil'}
                          </span>
                        </Link>
                        <button
                          onClick={() => { handleLogOut(); setMobileOpen(false) }}
                          className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition-colors text-sm font-medium"
                        >
                          <LogOut className="w-4 h-4" />
                          Çıxış
                        </button>
                      </>
                    ) : (
                      <>
                        <Button asChild className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => setMobileOpen(false)}>
                          <AuthLink mode="register">
                            <UserPlus className="w-4 h-4 mr-2" />
                            {t('header.register') || 'Qeydiyyat'}
                          </AuthLink>
                        </Button>
                        <Button asChild variant="outline" className="w-full dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800" onClick={() => setMobileOpen(false)}>
                          <AuthLink mode="login">
                            <LogIn className="w-4 h-4 mr-2" />
                            {t('header.login') || 'Giriş'}
                          </AuthLink>
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
